import { getInAppWebview } from "./detect-webview";
import { FETCH_FAILURE_REASON, type FetchFailureReason } from "./fetch-failure";
import type { LogEvent } from "~/hooks/useLogEvent";

export const KEEPALIVE_LOG_EVENT = {
    TAG: "keepalive-refused",
    REFUSED: "A keepalive request was refused and is sent again without keepalive",
    RESEND_SETTLED: "A refused keepalive request sent again without keepalive settled",
} as const;

/** The code that sent a refused request, in the refusal log. */
export const KEEPALIVE_SENDER = {
    FETCH_BASE: "fetch_base",
    GUEST_EVENT: "guest_event",
    EVENT_BATCH: "event_batch",
} as const;

export type KeepaliveSender = (typeof KEEPALIVE_SENDER)[keyof typeof KEEPALIVE_SENDER];

/** How the request sent again without keepalive ended, in the refusal log. */
export const KEEPALIVE_RESEND_OUTCOME = {
    /** An HTTP response came back: the API's, or a 5xx or an edge page (see `status`). */
    DELIVERED: "delivered",
    /** Cut off as the page was left (`keepalive_refused_on_exit`): the event is lost. */
    CUT_OFF: "cut_off",
    FAILED: "failed",
} as const;

export type KeepaliveResendOutcome = (typeof KEEPALIVE_RESEND_OUTCOME)[keyof typeof KEEPALIVE_RESEND_OUTCOME];

/**
 * The share of refusals logged. Datadog sends its logs through the same 64 KiB
 * keepalive quota, so logging every refusal would add to what causes them.
 */
const KEEPALIVE_REFUSAL_LOG_SAMPLE_RATE = 0.1;

export type KeepaliveRefusalTracker = {
    /** Pass to `fetchWithKeepaliveFallback`: gives the budget back and logs the refusal. */
    onRefused: () => void;
    wasRefused: () => boolean;
    /** Logs the HTTP status the request sent again got back. Only the first report counts. */
    reportDelivered: (status: number) => void;
    /** Logs why the request sent again failed. Only the first report counts. */
    reportFailed: (failureReason: FetchFailureReason) => void;
};

/**
 * Follows one send through a possible refusal. A sampled refusal is logged as
 * it happens, while the page is still there, and again once the request sent
 * again settles; a refusal with no outcome is one the page left before its
 * log could go out. Without `logEvent` it only tracks.
 */
export function trackKeepaliveRefusal({
    sender,
    release,
    logEvent,
}: {
    sender: KeepaliveSender;
    release?: () => void;
    logEvent?: LogEvent;
}): KeepaliveRefusalTracker {
    let isRefused = false;
    let isSampled = false;
    let isSettled = false;

    const logSettled = (
        outcome: KeepaliveResendOutcome,
        { status, failureReason }: { status?: number; failureReason?: FetchFailureReason }
    ) => {
        if (!isSampled || isSettled) return;
        isSettled = true;
        logEvent?.({
            message: KEEPALIVE_LOG_EVENT.RESEND_SETTLED,
            tag: KEEPALIVE_LOG_EVENT.TAG,
            data: { sender, outcome, status, failure_reason: failureReason },
        });
    };

    return {
        onRefused: () => {
            isRefused = true;
            release?.();
            isSampled = logEvent !== undefined && Math.random() < KEEPALIVE_REFUSAL_LOG_SAMPLE_RATE;
            if (!isSampled) return;
            logEvent?.({
                message: KEEPALIVE_LOG_EVENT.REFUSED,
                tag: KEEPALIVE_LOG_EVENT.TAG,
                data: {
                    sender,
                    visibility_state: typeof document !== "undefined" ? document.visibilityState : undefined,
                    in_app_webview: getInAppWebview(typeof navigator !== "undefined" ? navigator.userAgent : undefined),
                },
            });
        },
        wasRefused: () => isRefused,
        reportDelivered: (status) => logSettled(KEEPALIVE_RESEND_OUTCOME.DELIVERED, { status }),
        reportFailed: (failureReason) =>
            logSettled(
                failureReason === FETCH_FAILURE_REASON.KEEPALIVE_REFUSED_ON_EXIT
                    ? KEEPALIVE_RESEND_OUTCOME.CUT_OFF
                    : KEEPALIVE_RESEND_OUTCOME.FAILED,
                { failureReason }
            ),
    };
}
